import React, { useState, useCallback } from "react";
import { Battery, Zap, RotateCcw, Play, Eye, EyeOff } from "lucide-react";

const BatteryGame = () => {
  // 生成所有可能的有电电池组合（从10个位置中选5个）
  const generateAllChargedCombinations = () => {
    const combinations = [];
    
    // 递归生成所有C(10,5)的组合
    const generateCombinations = (start, current, remaining) => {
      if (remaining === 0) {
        combinations.push([...current]);
        return;
      }
      
      for (let i = start; i <= 10 - remaining; i++) {
        current.push(i);
        generateCombinations(i + 1, current, remaining - 1);
        current.pop();
      }
    };
    
    generateCombinations(0, [], 5);
    return combinations;
  };

  // 初始化电池数组
  const initializeBatteries = (mode = "normal") => {
    const batteries = Array.from({ length: 10 }, (_, index) => ({
      id: index,
      hasCharge: (mode === "evil" || mode === "challenge") ? null : index < 5, // 恶意模式和挑战模式下初始为null
      isSelected: false,
      testResults: [], // 记录该电池参与的测试结果
    }));

    // 普通模式下随机打乱电池的有电状态
    if (mode === "normal") {
      for (let i = batteries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tempCharge = batteries[i].hasCharge;
        batteries[i].hasCharge = batteries[j].hasCharge;
        batteries[j].hasCharge = tempCharge;
      }
    }

    return batteries;
  };

  const [gameMode, setGameMode] = useState("normal"); // "normal", "challenge", "evil"
  const [batteries, setBatteries] = useState(() => initializeBatteries("normal"));
  const [attemptCount, setAttemptCount] = useState(0);
  const [testHistory, setTestHistory] = useState([]); // 测试历史
  const [gameOver, setGameOver] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showTestCount, setShowTestCount] = useState(false); // 是否显示测试次数
  const [chargedCount, setChargedCount] = useState(5); // 跟踪已分配的有电电池数量
  const [deadCount, setDeadCount] = useState(5); // 跟踪已分配的没电电池数量
  
  // 恶意模式相关状态
  const [possibleChargedCombinations, setPossibleChargedCombinations] = useState([]);

  // 恶意模式：排除会让玩家成功的组合
  const filterEvilCombinations = useCallback((selectedIds) => {
    if (gameMode !== "evil") return { success: false, remainingCombinations: [] };

    // 筛选出不会让玩家成功的组合（两个选中位置不会都有电）
    const newCombinations = possibleChargedCombinations.filter((combination) => {
      // 检查这个组合是否会让玩家成功（两个位置都有电）
      const wouldSucceed = selectedIds.every((pos) => combination.includes(pos));
      return !wouldSucceed; // 保留不会成功的组合
    });

    setPossibleChargedCombinations(newCombinations);
    
    // 返回是否成功和剩余组合信息
    return {
      success: newCombinations.length === 0,
      remainingCombinations: newCombinations,
      previousCombinations: possibleChargedCombinations
    };
  }, [gameMode, possibleChargedCombinations]);

  // 挑战模式：智能分配电池状态
  const assignBatteryStates = useCallback(
    (selectedIds) => {
      setBatteries((prev) => {
        const newBatteries = [...prev];

        // 找出需要分配状态的电池（hasCharge为null的）
        const needAssignment = selectedIds.filter((id) => {
          const battery = newBatteries.find((b) => b.id === id);
          return battery.hasCharge === null;
        });

        // 如果没有需要分配的电池，直接返回
        if (needAssignment.length === 0) {
          return newBatteries;
        }

        // 找出已经分配状态的电池
        const alreadyAssigned = selectedIds.filter((id) => {
          const battery = newBatteries.find((b) => b.id === id);
          return battery.hasCharge !== null;
        });

        let currentChargedCount = chargedCount;
        let currentDeadCount = deadCount;

        // 检查已分配电池的状态
        const assignedCharged = alreadyAssigned.filter((id) => {
          const battery = newBatteries.find((b) => b.id === id);
          return battery.hasCharge === true;
        }).length;

        const assignedDead = alreadyAssigned.filter((id) => {
          const battery = newBatteries.find((b) => b.id === id);
          return battery.hasCharge === false;
        }).length;

        // 挑战模式策略：尽可能让用户失败
        if (needAssignment.length === 1) {
          // 只需要分配一个电池
          const batteryId = needAssignment[0];
          const battery = newBatteries.find((b) => b.id === batteryId);

          if (assignedCharged > 0) {
            // 如果已选择的电池中有有电的，新电池分配没电
            if (currentDeadCount > 0) {
              battery.hasCharge = false;
              setDeadCount((prev) => prev - 1);
            } else {
              battery.hasCharge = true;
              setChargedCount((prev) => prev - 1);
            }
          } else if (assignedDead > 0) {
            // 如果已选择的电池中有没电的，新电池可以随机分配，但倾向于没电
            if (currentDeadCount > 0 && Math.random() < 0.7) {
              battery.hasCharge = false;
              setDeadCount((prev) => prev - 1);
            } else if (currentChargedCount > 0) {
              battery.hasCharge = true;
              setChargedCount((prev) => prev - 1);
            } else {
              battery.hasCharge = false;
              setDeadCount((prev) => prev - 1);
            }
          }
        } else if (needAssignment.length === 2) {
          // 需要分配两个电池
          // 分配策略：优先让1个有电1个没电，保持挑战性
          const canAssignOneOfEach =
            currentChargedCount >= 1 && currentDeadCount >= 1;
          const shouldAssignOneOfEach =
            canAssignOneOfEach && Math.random() < 0.7; // 70%概率分配1有1没

          if (shouldAssignOneOfEach) {
            // 1个有电，1个没电
            const chargedIndex = Math.floor(Math.random() * 2);
            needAssignment.forEach((id, index) => {
              const battery = newBatteries.find((b) => b.id === id);
              battery.hasCharge = index === chargedIndex;
            });
            setChargedCount((prev) => prev - 1);
            setDeadCount((prev) => prev - 1);
          } else if (currentDeadCount >= 2) {
            // 都没电
            needAssignment.forEach((id) => {
              const battery = newBatteries.find((b) => b.id === id);
              battery.hasCharge = false;
            });
            setDeadCount((prev) => prev - 2);
          } else if (canAssignOneOfEach) {
            // 修复：如果没电的不够2个，但还能分配1有1没，强制这样做来保持挑战性
            const chargedIndex = Math.floor(Math.random() * 2);
            needAssignment.forEach((id, index) => {
              const battery = newBatteries.find((b) => b.id === id);
              battery.hasCharge = index === chargedIndex;
            });
            setChargedCount((prev) => prev - 1);
            setDeadCount((prev) => prev - 1);
          } else if (currentDeadCount >= 1) {
            // 如果只能分配1个没电的，至少让一个失败
            needAssignment.forEach((id, index) => {
              const battery = newBatteries.find((b) => b.id === id);
              battery.hasCharge = index === 1; // 第一个没电，第二个有电
            });
            setChargedCount((prev) => prev - 1);
            setDeadCount((prev) => prev - 1);
          } else {
            // 最后的备选方案：实在没有没电的电池了，只能分配都有电
            needAssignment.forEach((id) => {
              const battery = newBatteries.find((b) => b.id === id);
              battery.hasCharge = true;
            });
            setChargedCount((prev) => prev - 2);
          }
        }

        return newBatteries;
      });
    },
    [chargedCount, deadCount]
  );

  // 检查恶意模式下电池状态是否已确定
  const isEvilModeStateUndetermined = gameMode === "evil" && !gameOver && possibleChargedCombinations.length > 1;

  // 选择/取消选择电池
  const toggleBattery = useCallback(
    (id) => {
      if (gameOver || showAnswer) return; // 显示答案时禁用选择

      setBatteries((prev) => {
        const selectedCount = prev.filter((b) => b.isSelected).length;
        const battery = prev.find((b) => b.id === id);

        // 如果要选择第3个电池，不允许
        if (!battery.isSelected && selectedCount >= 2) return prev;

        return prev.map((b) =>
          b.id === id ? { ...b, isSelected: !b.isSelected } : b
        );
      });
    },
    [gameOver, showAnswer]
  );

  // 测试选中的电池
  const testBatteries = useCallback(() => {
    const selectedBatteries = batteries.filter((b) => b.isSelected);

    if (selectedBatteries.length !== 2) {
      alert("要选2个电池哦！");
      return;
    }

    const selectedIds = selectedBatteries.map((b) => b.id);

    // 恶意模式：排除会成功的组合，不立即确定电池状态
    if (gameMode === "evil" && !gameOver) {
      // 先排除会成功的组合，获取结果信息
      const filterResult = filterEvilCombinations(selectedIds);
      
      const currentAttempt = attemptCount + 1;
      let allHaveCharge = false;
      
      // 如果没有剩余组合，说明玩家必然成功，锁定状态
      if (filterResult.success) {
        // 玩家成功了，从之前的剩余组合中选择一个让当前测试成功的组合
        // 这里要从过滤前的组合中选择，确保与之前的测试结果一致
        const validCombinations = filterResult.previousCombinations.filter((combination) => 
          selectedIds.every((pos) => combination.includes(pos))
        );
        
        if (validCombinations.length > 0) {
          const randomCombination = validCombinations[Math.floor(Math.random() * validCombinations.length)];
          
          setBatteries(current => {
            const updatedBatteries = current.map(battery => ({
              ...battery,
              hasCharge: randomCombination.includes(battery.id),
              isSelected: false,
              testResults: selectedIds.includes(battery.id) ? [
                ...battery.testResults,
                { attempt: currentAttempt, success: true }
              ] : battery.testResults
            }));
            
            setGameOver(true);
            allHaveCharge = true;
            
            setTestHistory(prev => [...prev, {
              attempt: currentAttempt,
              batteries: selectedIds.map(id => ({
                id,
                hasCharge: true,
                isSelected: false,
                testResults: []
              })),
              success: true
            }]);
            
            return updatedBatteries;
          });
        }
      } else {
        // 还有可能的组合，这次测试失败
        allHaveCharge = false;
        
        setBatteries(current => current.map(battery => ({
          ...battery,
          isSelected: false,
          testResults: selectedIds.includes(battery.id) ? [
            ...battery.testResults,
            { attempt: currentAttempt, success: false }
          ] : battery.testResults
        })));
        
        setTestHistory(prev => [...prev, {
          attempt: currentAttempt,
          batteries: selectedIds.map(id => ({
            id,
            hasCharge: false, // 在恶意模式下显示为失败
            isSelected: false,
            testResults: []
          })),
          success: false
        }]);
      }
      
      setAttemptCount(currentAttempt);
      return;
    }

    // 挑战模式：动态分配状态
    if (gameMode === "challenge" && !gameOver) {
      // 检查是否还需要分配状态
      const needsAssignment = selectedBatteries.some(
        (b) => b.hasCharge === null
      );
      if (needsAssignment) {
        // 分配状态
        assignBatteryStates(selectedIds);

        // 等待状态更新后再处理测试结果
        setTimeout(() => {
          setBatteries((current) => {
            const currentAttempt = attemptCount + 1;
            const allHaveCharge = selectedIds.every((id) => {
              const battery = current.find((b) => b.id === id);
              return battery.hasCharge === true;
            });

            // 增加尝试次数
            setAttemptCount(currentAttempt);

            // 记录测试历史
            setTestHistory((prev) => [
              ...prev,
              {
                attempt: currentAttempt,
                batteries: selectedIds.map((id) =>
                  current.find((b) => b.id === id)
                ),
                success: allHaveCharge,
              },
            ]);

            // 更新电池的测试结果记录并取消选择
            const updatedBatteries = current.map((b) => {
              if (selectedIds.includes(b.id)) {
                return {
                  ...b,
                  isSelected: false,
                  testResults: [
                    ...b.testResults,
                    { attempt: currentAttempt, success: allHaveCharge },
                  ],
                };
              }
              return b;
            });

            if (allHaveCharge) {
              setGameOver(true);
            }

            return updatedBatteries;
          });
        }, 100);
        return;
      }
    }

    // 普通模式或电池状态已分配的情况
    const allHaveCharge = selectedBatteries.every((b) => b.hasCharge);
    const currentAttempt = attemptCount + 1;

    // 记录测试历史
    setTestHistory((prev) => [
      ...prev,
      {
        attempt: currentAttempt,
        batteries: selectedBatteries.map((b) => ({ ...b, isSelected: false })),
        success: allHaveCharge,
      },
    ]);

    // 更新电池的测试结果记录
    setBatteries((prev) =>
      prev.map((b) => {
        if (b.isSelected) {
          return {
            ...b,
            isSelected: false,
            testResults: [
              ...b.testResults,
              { attempt: currentAttempt, success: allHaveCharge },
            ],
          };
        }
        return b;
      })
    );

    setAttemptCount(currentAttempt);

    if (allHaveCharge) {
      setGameOver(true);
    }
          }, [batteries, attemptCount, gameMode, gameOver, assignBatteryStates, filterEvilCombinations]);

  // 重置游戏
  const resetGame = useCallback(() => {
    const newBatteries = initializeBatteries(gameMode);
    setBatteries(newBatteries);
    setAttemptCount(0);
    setTestHistory([]);
    setGameOver(false);
    setShowAnswer(false);
    setChargedCount(5);
    setDeadCount(5);
    
    // 恶意模式需要初始化所有可能的组合
    if (gameMode === "evil") {
      const allCombinations = generateAllChargedCombinations();
      setPossibleChargedCombinations(allCombinations);
    }
  }, [gameMode]);

  // 切换模式
  const changeMode = useCallback((newMode) => {
    setGameMode(newMode);
    // 切换模式后重置游戏
    const newBatteries = initializeBatteries(newMode);
    setBatteries(newBatteries);
    setAttemptCount(0);
    setTestHistory([]);
    setGameOver(false);
    setShowAnswer(false);
    setChargedCount(5);
    setDeadCount(5);
    
    // 恶意模式需要初始化所有可能的组合
    if (newMode === "evil") {
      const allCombinations = generateAllChargedCombinations();
      setPossibleChargedCombinations(allCombinations);
    }
  }, []);

  // 计算状态
  const selectedCount = batteries.filter((b) => b.isSelected).length;

  // 获取模式描述
  const getModeDescription = () => {
    switch (gameMode) {
      case "normal":
        return "😊 普通模式：电池好坏随机分布，靠运气和智慧！";
      case "challenge":
        return "😤 挑战模式：前几次故意让你失败，看你能不能坚持！";
      case "evil":
        return "😈 恶意模式：系统维护所有可能的电池组合，每次测试时排除会让你成功的组合，直到无法避免你成功为止！";
      default:
        return "";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-5xl font-bold text-center text-purple-800 mb-4">
          🔦 手电筒电池游戏 🔋
        </h1>
        <p className="text-xl text-center text-gray-600 mb-8">
          帮手电筒找到能亮的电池吧！
        </p>

        {/* 游戏设置 */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-4 border-purple-200">
          <div className="flex flex-col space-y-4">
            {/* 模式选择 */}
            <div className="flex flex-col space-y-3">
              <h3 className="text-lg font-bold text-center text-gray-700">🎮 选择游戏模式</h3>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  onClick={() => changeMode("normal")}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    gameMode === "normal"
                      ? "bg-green-500 text-white shadow-lg scale-105"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  🌟 普通模式
                </button>
                <button
                  onClick={() => changeMode("challenge")}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    gameMode === "challenge"
                      ? "bg-orange-500 text-white shadow-lg scale-105"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  😤 挑战模式
                </button>
                <button
                  onClick={() => changeMode("evil")}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    gameMode === "evil"
                      ? "bg-red-500 text-white shadow-lg scale-105"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  😈 恶意模式
                </button>
              </div>
            </div>

            {/* 显示测试次数开关 */}
            <div className="flex items-center justify-center space-x-4 pt-2 border-t border-gray-200">
              <span className="text-lg font-medium text-gray-700 whitespace-nowrap">
                📊 显示测试次数
              </span>
              <button
                onClick={() => setShowTestCount(!showTestCount)}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  showTestCount ? "bg-blue-400" : "bg-gray-300"
                } shadow-lg`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md ${
                    showTestCount ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
          
          <div className="text-center mt-4 text-base text-gray-500">
            {getModeDescription()}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：游戏区域 */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6 border-4 border-blue-200">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-blue-800 mb-4">
                🎯 游戏任务
              </h2>
              <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                <p className="text-lg text-blue-700 mb-2">
                  📦 这里有10个电池，5个有电，5个没电，长得一模一样！
                </p>
                <p className="text-lg text-blue-700">
                  🔍 每次选2个电池放进手电筒，如果都有电，手电筒就会亮！
                </p>
              </div>
            </div>

            {/* 电池网格 */}
            <div className="grid grid-cols-5 gap-3 max-w-lg mx-auto mb-8">
              {batteries.map((battery) => {
                const hasBeenTested = battery.testResults.length > 0;
                const lastResult = hasBeenTested
                  ? battery.testResults[battery.testResults.length - 1]
                  : null;

                return (
                  <button
                    key={battery.id}
                    onClick={() => toggleBattery(battery.id)}
                    disabled={gameOver || showAnswer}
                    className={`
                      h-24 w-20 rounded-2xl border-4 transition-all duration-200 flex flex-col items-center justify-center relative
                      ${
                        gameOver || showAnswer
                          ? "cursor-not-allowed opacity-60"
                          : battery.isSelected
                          ? "bg-yellow-200 border-yellow-500 shadow-lg scale-105"
                          : hasBeenTested
                          ? lastResult.success
                            ? "bg-green-100 border-green-400 hover:bg-green-200"
                            : "bg-red-100 border-red-400 hover:bg-red-200"
                          : "bg-gray-100 border-gray-400 hover:bg-blue-100 hover:border-blue-500"
                      }
                    `}
                  >
                    <Battery
                      size={28}
                      className={`
                        ${
                          battery.isSelected
                            ? "text-yellow-700"
                            : showAnswer && battery.hasCharge !== null
                            ? battery.hasCharge
                              ? "text-green-600"
                              : "text-red-600"
                            : hasBeenTested
                            ? lastResult.success
                              ? "text-green-700"
                              : "text-red-700"
                            : "text-gray-600"
                        }
                      `}
                    />
                    <span className="text-xs font-bold text-gray-600 mt-1">
                      {battery.id + 1}号
                    </span>
                    {showAnswer && battery.hasCharge !== null && (
                      <span
                        className={`text-xs font-bold mt-1 ${
                          battery.hasCharge ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {battery.hasCharge ? "有电" : "没电"}
                      </span>
                    )}
                    {showTestCount && hasBeenTested && (
                      <div className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold shadow-lg">
                        {battery.testResults.length}
                      </div>
                    )}
                    {battery.isSelected && (
                      <div className="absolute -top-1 -left-1 text-yellow-500">
                        ⭐
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 状态显示 */}
            <div className="text-center mb-8">
              <div className="flex justify-center space-x-12 mb-6">
                <div className="text-center bg-yellow-50 rounded-xl p-4 border-2 border-yellow-300">
                  <div className="text-3xl font-bold text-yellow-600">
                    {selectedCount}
                  </div>
                  <div className="text-sm text-yellow-700 font-medium">
                    ⭐ 已选择
                  </div>
                </div>
                <div className="text-center bg-green-50 rounded-xl p-4 border-2 border-green-300">
                  <div className="text-3xl font-bold text-green-600">
                    {attemptCount}
                  </div>
                  <div className="text-sm text-green-700 font-medium">
                    🚀 尝试次数
                  </div>
                </div>
              </div>

              {showAnswer && (
                <div className="mt-4 text-center">
                  <p className="text-orange-600 font-medium">
                    ⚠️
                    偷看答案后就不能继续测试了哦！要想继续玩就点"重新开始"吧！
                  </p>
                </div>
              )}

              {gameOver && (
                <div className="bg-gradient-to-r from-green-100 to-blue-100 border-4 border-green-400 p-6 rounded-2xl mb-6">
                  <div className="text-green-700 font-bold text-2xl">
                    🎉 太棒了！你成功了！🎊
                  </div>
                  <div className="text-green-600 mt-2 text-lg">
                    总共试了 {attemptCount} 次就找到了！
                  </div>
                  <div className="text-green-600 mt-2">
                    {attemptCount <= 2
                      ? "🌟 运气爆棚！"
                      : attemptCount <= 4
                      ? "👍 非常不错！"
                      : "💪 坚持就是胜利！"}
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-center space-x-4">
              <button
                onClick={testBatteries}
                disabled={selectedCount !== 2 || gameOver || showAnswer}
                className={`
                  px-8 py-4 rounded-2xl font-bold text-lg flex items-center space-x-3 transition-all
                  ${
                    selectedCount === 2 && !gameOver && !showAnswer
                      ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }
                `}
              >
                <Play size={24} />
                <span>测试电池</span>
              </button>

              <button
                onClick={resetGame}
                className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-2xl font-bold text-lg flex items-center space-x-3 transition-all shadow-lg"
              >
                <RotateCcw size={24} />
                <span>重新开始</span>
              </button>

              <button
                onClick={() => setShowAnswer(!showAnswer)}
                disabled={isEvilModeStateUndetermined}
                className={`px-8 py-4 rounded-2xl font-bold text-lg flex items-center space-x-3 transition-all shadow-lg ${
                  isEvilModeStateUndetermined 
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                }`}
              >
                {showAnswer ? <EyeOff size={24} /> : <Eye size={24} />}
                <span>{showAnswer ? "隐藏答案" : "偷看答案"}</span>
              </button>
            </div>
            
            {/* 恶意模式状态未确定的提示 */}
            {isEvilModeStateUndetermined && (
              <div className="mt-4 text-center">
                <p className="text-red-600 font-medium">
                  😈 恶意模式下电池状态还未确定，无法偷看答案！继续测试来逼迫系统确定状态吧！
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  剩余可能组合：{possibleChargedCombinations.length} 种
                </p>
              </div>
            )}
            
            {showAnswer && !isEvilModeStateUndetermined && (
              <div className="mt-4 text-center">
                <p className="text-orange-600 font-medium">
                  ⚠️ 偷看答案后就不能继续测试了哦！要想继续玩就点"重新开始"吧！
                </p>
              </div>
            )}
          </div>

          {/* 右侧：测试记录 */}
          <div className="bg-white rounded-2xl shadow-xl p-6 border-4 border-green-200">
            <h3 className="text-2xl font-bold mb-6 text-green-800 text-center">
              📝 测试记录
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testHistory.length === 0 ? (
                <div className="text-gray-500 text-center py-12">
                  <div className="text-6xl mb-4">🤔</div>
                  <div className="text-lg">还没有开始测试哦</div>
                  <div className="text-sm text-gray-400 mt-2">
                    赶快选2个电池试试吧！
                  </div>
                </div>
              ) : (
                testHistory.map((test, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      test.success
                        ? "bg-green-50 border-green-300"
                        : "bg-red-50 border-red-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-lg">
                        第 {test.attempt} 次
                      </span>
                      <span
                        className={`text-lg font-bold ${
                          test.success ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {test.success ? "✅ 成功了！" : "❌ 失败了"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      🔋 测试了：
                      {test.batteries.map((b) => `${b.id + 1}号`).join(", ")}
                    </div>
                    {test.success && (
                      <div className="mt-2 text-xs text-green-700 font-medium">
                        🎊 手电筒亮了！找到有电的电池了！
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default BatteryGame;
